/**
 * Per-camera alert priority.
 *
 * A camera carries a priority; every alert it raises has that priority snapshotted onto
 * it by the API at insert time, the same way `alert_location` / `alert_location_color`
 * already are. Kept in one place — the same mapping is read by the Cameras drawer, the
 * queue's card fill and the settings drawer, and three copies is how a rename ends up
 * showing "High" in one view and "high" in another.
 *
 * Priority decides how an alert is COLOURED, not where it sits: the queue is
 * chronological on every tab. The API still stores `alert_priority_rank` and can still
 * order by it — this client simply does not ask it to.
 */

export type AlertPriority = 'high' | 'medium' | 'low';

/**
 * What an unset priority means. One place, because it is a product decision — and it must
 * stay in step with DEFAULT_ALERT_PRIORITY in the API's utils/alert-priority.js, or the
 * client and the server disagree about where un-stamped alerts belong.
 *
 * Low, not medium: priority is an escalation, so a camera sits at the bottom of the scale
 * until someone deliberately lifts it above its peers.
 */
export const DEFAULT_ALERT_PRIORITY: AlertPriority = 'low';

// The API's ranks, kept as the set of valid levels the two normalisers check against.
// Mirrors ALERT_PRIORITY_RANK in the API's utils/alert-priority.js.
const ALERT_PRIORITY_RANK: Record<AlertPriority, number> = {
  high: 30,
  medium: 20,
  low: 10,
};

/**
 * The segmented control's tiles, most urgent first. The arrow icons carry the direction
 * of the scale, so the control still reads as high-to-low for anyone who cannot separate
 * the three fills by colour alone.
 */
export const ALERT_PRIORITY_OPTIONS: Array<{ value: AlertPriority; label: string; icon: string }> = [
  { value: 'high', label: 'High', icon: 'fa-angle-double-up' },
  { value: 'medium', label: 'Medium', icon: 'fa-minus' },
  { value: 'low', label: 'Low', icon: 'fa-angle-double-down' },
];

/**
 * Canonicalise whatever arrives. The API can send `null`, and the alert projection has a
 * history of delivering the literal string 'null' — see getLocationColor in the alert
 * queue, which already has to defend against it.
 */
export function normalizeAlertPriority(raw: unknown): AlertPriority {
  const key = String(raw ?? '').trim().toLowerCase();
  return (ALERT_PRIORITY_RANK as Record<string, number>)[key] !== undefined
    ? (key as AlertPriority)
    : DEFAULT_ALERT_PRIORITY;
}

/**
 * Strict counterpart to normalizeAlertPriority: null — not the default — for anything
 * unrecognised, mirroring normalisePriority in the API's utils/alert-priority.js.
 *
 * The card fill needs this distinction. Every camera has a priority, so an alert without
 * `alert_priority` is one written before the feature and never backfilled; colouring it
 * with the default would claim a level nobody set and hide the location colour it does
 * have.
 */
export function toAlertPriority(raw: unknown): AlertPriority | null {
  const key = String(raw ?? '').trim().toLowerCase();
  return (ALERT_PRIORITY_RANK as Record<string, number>)[key] !== undefined
    ? (key as AlertPriority)
    : null;
}

/**
 * Font Awesome class for a priority, so the Cameras drawer's control and the settings
 * drawer show the same arrow for the same level. Read from the options list rather than a
 * second map — one place decides which glyph means "high".
 */
export function getAlertPriorityIcon(priority: unknown): string {
  const value = normalizeAlertPriority(priority);
  return ALERT_PRIORITY_OPTIONS.find((option) => option.value === value)!.icon;
}
